import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Snackbar,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { getFaqs, createFaq, updateFaq, deleteFaq } from '../../services/api/contentApi';
import { FAQ, FaqFormData } from '../../types/content';
import { formatDateTime } from '../../utils/formatters';

const FaqPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    category?: string;
    search?: string;
  }>({
    page: 0,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FAQ | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemId: string | null;
  }>({
    open: false,
    itemId: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FaqFormData>({
    defaultValues: {
      question: '',
      answer: '',
      category: '',
      order: 0,
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['faqs', filters],
    queryFn: () => getFaqs(filters),
  });

  const createMutation = useMutation({
    mutationFn: createFaq,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      setSnackbar({
        open: true,
        message: 'FAQ가 추가되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'FAQ 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FaqFormData> }) => updateFaq(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      setSnackbar({
        open: true,
        message: 'FAQ가 수정되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'FAQ 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFaq,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      setSnackbar({
        open: true,
        message: 'FAQ가 삭제되었습니다.',
        severity: 'success',
      });
      setConfirmDialog({ open: false, itemId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'FAQ 삭제에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    reset({
      question: '',
      answer: '',
      category: '',
      order: 0,
    });
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item: FAQ) => {
    setEditingItem(item);
    reset({
      question: item.question,
      answer: item.answer,
      category: item.category,
      order: item.order,
    });
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingItem(null);
    reset();
  };

  const handleFormSubmit = (data: FaqFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteClick = (id: string) => {
    setConfirmDialog({ open: true, itemId: id });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.itemId) {
      deleteMutation.mutate(confirmDialog.itemId);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, itemId: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<FAQ>[] = [
    {
      id: 'order',
      label: '순서',
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'category',
      label: '카테고리',
      minWidth: 120,
    },
    {
      id: 'question',
      label: '질문',
      minWidth: 300,
    },
    {
      id: 'isActive',
      label: '상태',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '활성' : '비활성'} />
      ),
    },
    {
      id: 'createdAt',
      label: '생성일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: FAQ) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(row)}>
            수정
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(row._id)}
          >
            삭제
          </Button>
        </Stack>
      ),
    },
  ];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = () => {
    setFilters(prev => ({
      ...prev,
      search: searchInput || undefined,
      page: 0,
    }));
  };

  const handleCategoryFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      category: value === 'all' ? undefined : value,
      page: 0,
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">FAQ 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
          FAQ 추가
        </Button>
      </Box>

      {/* 필터 및 검색 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="검색 (질문, 답변)"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
            size="small"
            sx={{ minWidth: 300 }}
            placeholder="검색어를 입력하세요"
          />
          <Button variant="contained" onClick={handleSearchSubmit}>
            검색
          </Button>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel size="small">카테고리</InputLabel>
            <Select
              value={filters.category || 'all'}
              label="카테고리"
              onChange={e => handleCategoryFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="닥터피싱">닥터피싱</MenuItem>
              <MenuItem value="몸캠피싱">몸캠피싱</MenuItem>
              <MenuItem value="보이스피싱">보이스피싱</MenuItem>
              <MenuItem value="딥페이크">딥페이크</MenuItem>
              <MenuItem value="악성 앱 탐지 및 제거">악성 앱 탐지 및 제거</MenuItem>
              <MenuItem value="지원">지원</MenuItem>
            </Select>
          </FormControl>

          {(filters.search || filters.category) && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearchInput('');
                setFilters(prev => ({ 
                  ...prev, 
                  search: undefined, 
                  category: undefined, 
                  page: 0 
                }));
              }}
            >
              초기화
            </Button>
          )}
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={(data?.items || []).map(item => ({ ...item, id: item._id }))}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={isLoading}
      />

      {/* 추가/수정 폼 다이얼로그 */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'FAQ 수정' : 'FAQ 추가'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="category"
                control={control}
                rules={{ required: '카테고리는 필수입니다' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.category}>
                    <InputLabel>카테고리</InputLabel>
                    <Select {...field} label="카테고리">
                      <MenuItem value="닥터피싱">닥터피싱</MenuItem>
                      <MenuItem value="몸캠피싱">몸캠피싱</MenuItem>
                      <MenuItem value="보이스피싱">보이스피싱</MenuItem>
                      <MenuItem value="딥페이크">딥페이크</MenuItem>
                      <MenuItem value="악성 앱 탐지 및 제거">악성 앱 탐지 및 제거</MenuItem>
                      <MenuItem value="지원">지원</MenuItem>
                    </Select>
                    {errors.category && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.category.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="question"
                control={control}
                rules={{ required: '질문은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="질문"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.question}
                    helperText={errors.question?.message}
                  />
                )}
              />
              <Controller
                name="answer"
                control={control}
                rules={{ required: '답변은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="답변"
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.answer}
                    helperText={errors.answer?.message}
                  />
                )}
              />
              <Controller
                name="order"
                control={control}
                rules={{ required: '순서는 필수입니다', min: 0 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="순서"
                    type="number"
                    fullWidth
                    error={!!errors.order}
                    helperText={errors.order?.message}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>취소</Button>
            <Button type="submit" variant="contained">
              {editingItem ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="FAQ 삭제"
        message="이 FAQ를 삭제하시겠습니까?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="삭제"
        confirmColor="error"
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FaqPage;
